<template>
  <div>
    <label v-if="label" class="percent-input__label">{{ label }}</label>
    <div
      class="amount-input-group percent-input"
      :class="{ disabled, 'has-error': !!error }"
    >
      <base-input class="amount-input-wrapper mb-0">
        <el-input-number
          :controls="false"
          :value="value"
          :min="min"
          :max="max"
          :precision="precision"
          :step="step"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="onInput"
          @change="onChange"
        />
      </base-input>
      <span class="amount-prefix">%</span>
    </div>
    <small v-if="errorMessage" class="percent-input__error">{{ errorMessage }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

// Percentage field: the money-input chrome with a "%" suffix badge, bounded
// 0 to 100 by default. Emits null when cleared, like MoneyInput.
const props = defineProps({
  value: { type: Number as PropType<number | null>, default: null },
  label: { type: String, default: '' },
  min: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  precision: { type: Number, default: 0 },
  step: { type: Number, default: 1 },
  placeholder: { type: String, default: '0' },
  disabled: { type: Boolean, default: false },
  error: { type: [Boolean, String] as PropType<boolean | string>, default: false }
})

const emit = defineEmits<{
  (e: 'input', value: number | null): void
  (e: 'change', value: number | null): void
}>()

const errorMessage = computed(() => (typeof props.error === 'string' ? props.error : ''))

function onInput(val: number | null | undefined) {
  emit('input', val == null ? null : val)
}

function onChange(val: number | null | undefined) {
  emit('change', val == null ? null : val)
}
</script>

<script lang="ts">
export default { name: 'PercentInput' }
</script>

<style lang="scss" scoped>
.percent-input__label {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8571em;
  margin-bottom: 5px;
}

.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: transparent;
  max-width: 160px;

  &:focus-within {
    border-color: #e14eca;
  }

  &.has-error {
    border-color: #ec250d;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .amount-prefix {
      background: rgba(225, 78, 202, 0.08);
    }
  }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;
  min-width: 0;

  ::v-deep .form-control {
    border: none;
    border-radius: 0;
    background: transparent;
  }

  ::v-deep .el-input-number {
    width: 100%;
  }

  ::v-deep .el-input-number .el-input__inner {
    text-align: right;
    color: white;
    background: transparent;
    border: none;
  }
}

.percent-input__error {
  display: block;
  margin-top: 4px;
  color: #ec250d;
  font-size: 0.75rem;
}

.white-content {
  .percent-input__label {
    color: rgba(52, 70, 117, 0.7);
  }
  .amount-input-group {
    background: #ffffff;
    border-color: #d1d5db;
    &:focus-within { border-color: #e14eca; }
  }
  .amount-input-wrapper ::v-deep .el-input-number .el-input__inner {
    color: #344675;
  }
}
</style>
